import React, { CSSProperties, JSX } from 'react';

import { ScheduledEmailProps } from './create-email';
import { EmailTextPhrases, EmailTypeEnum, IScheduledEmailContent, IScheduledEmailSettings } from '@/types/email.types';
import { ColorOptions, ColorValueIndexes, FontPack, SafeFonts } from '@/types/theme-packs.types';
import { Templates } from '@/types/template.types';
import { LanguagesAbbr } from '@/types/translation.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import SessionEmailContent from './session-email-content';
import EventEmailContent from './event-email-content';

// these three variables must remain in sync with the same named variables in events-api and registration microservice
// if you make a change be sure to change in all repos
export const namePlaceholder = 'user_name';
export const regCodePlaceholder = '{{{regCode}}}';
const postmarkUnsubscribe = '{{{pm:unsubscribe }}}';

export interface EmailContent {
  translations: EmailTextPhrases;
  session?: {
    name: string;
    description: string;
    url: string;
    image: string;
    start: string | null;
    daysToStart?: number;
    // speakers?: {
    // 	name: string;
    // 	image: string;
    // 	social?: SocialLink[];
    // }[];
    icsFileContent?: { icsFileText: string; fileName: string };
  };
  event?: {
    name: string;
    description: string;
    start: string | null;
    registrationRequired: boolean;
    url: string;
    image: string;
    icsFileContent?: { icsFileText: string; fileName: string };
  };
  timezone?: string;
  settings?: IScheduledEmailSettings;
  calendarLinks?: EmailCalendarLinks;
}

export interface EmailCalendarLinks {
  iCal: string;
  outlook: string;
  google: string;
  content?: string;
  icsUrl?: string;
}

// defaults = colorPack 69 Classic
const defaultEmailColors: ColorOptions = {
  backgroundColor: ['#F5F7FF', 1],
  bodyTextColor: ['#081543', 1],
  containerColor: ['FFFFFF', 1],
  accentColor: ['#3456FF', 1],
  headingTextColor: ['#FFFFFF', 1],
  secondaryAccentColor: ['#FF8164', 1],
  secondaryBackgroundColor: ['#0E2B89', 1],
};

// reusable styling for all email types
export interface EmailDesign {
  emailBackground: string;
  containerBackground: string;
  containerRadius: string;
  bodyText: string;
  headingText: string;
  accentText: string;
  accentColor: string;
  buttonText: string;
  buttonRadius: string;
  styles: {
    p: CSSProperties;
    pSpan: CSSProperties;
    h3: CSSProperties;
    h3Span: CSSProperties;
    h4: CSSProperties;
    h4Span: CSSProperties;
    h6: CSSProperties;
    h6Span: CSSProperties;
    accent: CSSProperties;
    accentSpan: CSSProperties;
    primaryImage: CSSProperties;
    button: CSSProperties;
    link: CSSProperties;
  };
}

const isFontPack = (font: FontPack | string): font is FontPack => {
  return typeof font !== 'string';
};

const getEmailDesign = (
  template: Templates,
  colors: ColorOptions,
  fonts: FontPack | string,
  forceRtl?: boolean,
  anchorColor?: string
): EmailDesign => {
  const headingText =
    colors.headingTextColor[ColorValueIndexes.hex] ?? defaultEmailColors.headingTextColor[ColorValueIndexes.hex];
  const accentText =
    template === Templates.O2 && colors.accentColor[ColorValueIndexes.hex]
      ? colors.accentColor[ColorValueIndexes.hex]
      : (colors.secondaryAccentColor[ColorValueIndexes.hex] ??
        defaultEmailColors.secondaryAccentColor[ColorValueIndexes.hex]);
  const accentColor =
    colors.accentColor[ColorValueIndexes.hex] ?? defaultEmailColors.accentColor[ColorValueIndexes.hex];
  const buttonText =
    template === Templates['Studio 54']
      ? (colors.containerColor[ColorValueIndexes.hex] ?? '#FFFFFF')
      : (colors?.buttonTextColor?.[ColorValueIndexes.hex] ?? headingText);

  const bodyTextColor =
    colors.bodyTextColor[ColorValueIndexes.hex] ?? defaultEmailColors.bodyTextColor[ColorValueIndexes.hex];

  const buttonRadius = template === Templates.Apollo ? '12px' : template === Templates['Studio 54'] ? '100px' : '0px';

  const bodyFont = isFontPack(fonts) ? fonts.body_name : fonts;
  const headingFont = isFontPack(fonts) ? fonts.heading_name : fonts;

  return {
    emailBackground:
      colors.backgroundColor[ColorValueIndexes.hex] ??
      defaultEmailColors.secondaryBackgroundColor[ColorValueIndexes.hex],
    containerBackground:
      colors.containerColor[ColorValueIndexes.hex] ?? defaultEmailColors.containerColor[ColorValueIndexes.hex],
    containerRadius: template === Templates.Apollo ? '12px' : '0px',
    bodyText: bodyTextColor,
    headingText,
    accentText,
    accentColor,
    buttonText,
    buttonRadius,
    styles: {
      p: {
        textAlign: forceRtl ? 'right' : 'center',
        lineHeight: '1.7',
        fontFamily: `'${bodyFont ?? SafeFonts.Arial}', Arial, sans-serif`,
        margin: '0 0 15px 0',
        color: bodyTextColor,
      },
      pSpan: {
        fontSize: '15px',
        fontWeight: 400,
        color: bodyTextColor,
      },
      h3: {
        textAlign: forceRtl ? 'right' : 'center',
        lineHeight: '1.2',
        fontFamily: `'${headingFont ?? SafeFonts.Arial}', Arial, sans-serif`,
        margin: '0 0 15px 0',
        color: headingText,
      },
      h3Span: {
        fontSize: '25px',
        fontWeight: 700,
        color: headingText,
      },
      h4: {
        textAlign: forceRtl ? 'right' : 'center',
        lineHeight: '1.3',
        fontFamily: `'${headingFont ?? SafeFonts.Arial}', Arial, sans-serif`,
        margin: '0 0 10px 0',
        color: headingText,
      },
      h4Span: {
        fontSize: '20px',
        fontWeight: 700,
        color: headingText,
      },
      h6: {
        textAlign: forceRtl ? 'right' : 'center',
        lineHeight: '1.6',
        fontFamily: `'${headingFont ?? SafeFonts.Arial}', Arial, sans-serif`,
        margin: '0',
        color: headingText,
      },
      h6Span: {
        fontSize: '16px',
        fontWeight: 600,
        color: headingText,
      },
      accent: {
        textAlign: forceRtl ? 'right' : 'center',
        lineHeight: '1.4',
        fontFamily: `'${bodyFont ?? SafeFonts.Arial}', Arial, sans-serif`,
        textTransform: 'uppercase',
        margin: '0 0 15px 0',
        color: accentText,
      },
      accentSpan: {
        fontSize: '14px',
        fontWeight: 700,
        color: accentText,
      },
      primaryImage: {
        width: '100%',
        maxWidth: '540px',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        // FUTURE DEV: include this when we implement deleting email images.
        // "minHeight": "30px",
        display: 'block',
        border: '0',
        borderRadius: template === Templates.Apollo ? '12px' : '0px',
      },
      button: {
        border: '0',
        borderRadius: buttonRadius,
        backgroundColor: accentColor,
        padding: '15px 25px',
        color: buttonText,
        fontSize: '13px',
        lineHeight: '25px',
        fontWeight: 700,
        fontFamily: `'${headingFont ?? SafeFonts.Arial}', Arial, sans-serif`,
        textDecoration: template === Templates.O2 ? 'underline' : 'none',
        textTransform: template === Templates.Apollo ? 'uppercase' : 'none',
      },
      link: {
        color: anchorColor || '-webkit-link',
        textDecoration: 'underline',
        fontFamily: `'${bodyFont ?? SafeFonts.Arial}', Arial, sans-serif`,
      },
    },
  };
};

export interface EventEmailProps {
  emailType: EmailTypeEnum;
  template: Templates;
  colors: ColorOptions;
  fonts: FontPack | string;
  hideBrandliveLogo: boolean;
  clientLogo: string;
  emailContent: EmailContent;
  emailToSend: boolean;
  language?: LanguagesAbbr;
  customContent?: IScheduledEmailContent;
  onEdit?: ScheduledEmailProps['onEdit'];
  /** Keys are used so that when the jsx is changing, react doesnt try to get fancy and reuse some of the input values */
  idx?: string;
  sent?: boolean;
  forceRtl?: boolean;
  hideCalendar?: boolean;
  anchorColor?: string;
}

export const EventEmail = ({
  emailType,
  template,
  colors,
  fonts,
  hideBrandliveLogo,
  clientLogo,
  emailContent,
  emailToSend,
  customContent,
  onEdit,
  idx,
  sent,
  forceRtl,
  hideCalendar,
  anchorColor,
}: EventEmailProps): JSX.Element => {
  const design = getEmailDesign(template, colors, fonts, forceRtl, anchorColor);

  const hideUnsubscribe = emailType === EmailTypeEnum.event_registration_email;

  const emailTypeContent = (): JSX.Element => {
    if (emailType.includes('session')) {
      return (
        // Marketing Email
        <SessionEmailContent
          emailType={emailType}
          design={design}
          emailContent={emailContent}
          customContent={customContent}
          onEdit={onEdit}
          key={idx}
          sent={sent}
          forceRtl={forceRtl}
          hideCalendar={hideCalendar}
        />
      );
    } else {
      return (
        // Marketing Email
        <EventEmailContent
          emailType={emailType}
          design={design}
          emailContent={emailContent}
          customContent={customContent}
          onEdit={onEdit}
          key={idx}
          sent={sent}
          forceRtl={forceRtl}
          hideCalendar={hideCalendar}
        />
      );
    }
  };

  return (
    <table
      role="presentation"
      width="100%"
      cellSpacing="0"
      cellPadding="14px"
      style={{
        tableLayout: 'fixed',
        padding: '0',
        margin: '0',
        border: '0',
        backgroundColor: design.emailBackground,
        color: design.bodyText,
      }}
    >
      <tbody>
        <tr>
          <td align="center">
            <table
              role="presentation"
              width="100%"
              cellSpacing="0"
              cellPadding="10px"
              style={{
                tableLayout: 'fixed',
                maxWidth: '620px',
                padding: '0',
                margin: '0',
                border: '0',
                backgroundColor: design.emailBackground,
                color: design.bodyText,
              }}
            >
              <tbody>
                <Header clientLogo={clientLogo} />
                <tr>
                  <td>
                    <table
                      role="presentation"
                      width="100%"
                      cellSpacing="0"
                      cellPadding="30px"
                      style={{
                        tableLayout: 'fixed',
                        backgroundColor: design.containerBackground,
                        border: '0',
                        borderRadius: design.containerRadius,
                        color: design.bodyText,
                      }}
                    >
                      <tbody>
                        <tr>
                          <td>
                            <table
                              role="presentation"
                              width="100%"
                              cellSpacing="0"
                              cellPadding="0"
                              style={{ tableLayout: 'fixed', border: '0' }}
                            >
                              <tbody>{emailTypeContent()}</tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
                <Footer
                  translations={emailContent.translations}
                  emailToSend={emailToSend}
                  hideBrandliveLogo={hideBrandliveLogo}
                  hideUnsubscribe={hideUnsubscribe}
                />
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};

interface HeaderProps {
  clientLogo: string;
}

const Header = ({ clientLogo }: HeaderProps): JSX.Element => {
  return (
    <tr>
      <td align="center" valign="bottom" style={{ width: '100%' }}>
        {!!clientLogo.length && (
          <img
            src={clientLogo}
            alt="Company Logo"
            width="64"
            style={{ maxWidth: '64px', display: 'block', border: '0' }}
          />
        )}
      </td>
    </tr>
  );
};

interface FooterProps {
  translations: EmailTextPhrases;
  emailToSend: boolean;
  hideBrandliveLogo?: boolean;
  hideUnsubscribe?: boolean;
}

const Footer: React.FC<FooterProps> = ({
  /* translations, */ emailToSend,
  hideBrandliveLogo,
  hideUnsubscribe = false,
}) => {
  // const textPhrase = (key: keyof EmailTextPhrases) => {
  //   return translations[key];
  // };

  const brandliveLogo = 'https://assets.brandlive.com/assets/emails/invitation/brandlive_logo_grey.png';

  const footerTextStyle = {
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '16px',
    letterSpacing: '-0.2px',
    color: '#8F96B1',
    fontFamily: "'Roboto', Arial, sans-serif",
  };

  return (
    <>
      <tr>
        <td style={{ width: '100%', maxWidth: '600px' }}>
          <table
            role="presentation"
            width="100%"
            cellSpacing="0"
            cellPadding="0"
            style={{ tableLayout: 'fixed', marginBottom: '50px', border: '0', borderRadius: '12px' }}
          >
            <tbody>
              <tr>
                <td align="left" valign="middle" style={footerTextStyle}>
                  Sent by Brandlive Inc., 3303 N Mississippi Ave Ste 200, Portland, OR 97227
                </td>
                <td align="right" valign="middle" width="72px">
                  {!hideBrandliveLogo && (
                    <img
                      src={brandliveLogo}
                      alt="BrandLive"
                      width="56px"
                      style={{ maxWidth: '56px', display: 'block', border: '0' }}
                    />
                  )}
                </td>
              </tr>
              <OptionalComponent display={!hideUnsubscribe}>
                <tr>
                  <td align="left" height="36px" valign="bottom" style={footerTextStyle}>
                    {emailToSend ? postmarkUnsubscribe : 'Unsubscribe'}
                  </td>
                </tr>
              </OptionalComponent>
            </tbody>
          </table>
        </td>
      </tr>
    </>
  );
};
